import { type TFunction } from 'i18next';
import type {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid-pro';
import type { TGetDashboardByIdResponse } from 'utility/types/dataProvider';
import DashboardTypeChip from '../components/search/DashboardTypeChip';
import DashboardOwnerField from '../components/DashboardOwnerField';
import { SearchRowActionsCell } from 'components/Search/components/SearchRowActionsCell';

// =================================================
// Types.
// =================================================
export type DashboardGridColDef = GridColDef<TGetDashboardByIdResponse>;
export type TDashboard = Partial<TGetDashboardByIdResponse['data']>;
export type TRenderTitleCell = GridRenderCellParams<
  TGetDashboardByIdResponse,
  any,
  any,
  GridTreeNodeWithRender
>;

// =================================================
// Constants.
// =================================================
export const DASHBOARD_RESOURCE = 'insights/dashboard';

// For dashboard routing mapping ([dashboard id]: <dashboard path>).
export const DASHBOARD_MAP = {
  app: {
    core: 'home', // Home dashboard.
    editorial: 'editorial', // Content.
    subscription: 'user', // Community ("subscription" is needed in the getDashboards api call).
    emailreach: 'emailreach', // EmailReach.
  },
  page: {
    home: '',
    search: 'search',
    videoplays: 'dashboard/video',
    asset: 'asset', // editorial.asset
    reports: 'dashboard', // subscription.reports
    // ticker: 'dashboard', // emailreach.ticker
  },
};

// App filter choices based on DASHBOARD_MAP
export const getDashboardAppFilterChoices = (t: TFunction) => [
  {
    value: 'editorial',
    label: t(
      'blox.dashboards.custom_dashboards.search.app.options.editorial',
      'Editorial',
    ),
  },
  {
    value: 'subscription',
    label: t(
      'blox.dashboards.custom_dashboards.search.app.options.community',
      'Community',
    ),
  },
];

// For gridColumnDefs.tsx.
export function getDashboardSearchColumns(t: TFunction): DashboardGridColDef[] {
  return [
    {
      field: 'title',
      headerName: t('blox.dashboards.custom_dashboards.search.columns.title'),
      // TODO: enable sorting when backend supports it.
      sortable: true,
      resizable: true,
      flex: 2,
      display: 'text',
      type: 'string',
      minWidth: 250,
      hideable: false,
    },
    {
      // Status (values: “Public” for system dashboards; “Private” for all others)
      field: 'status',
      headerName: t('blox.dashboards.custom_dashboards.search.columns.status'),
      flex: 1,
      maxWidth: 250,
      sortable: false,
      renderCell: ({ row }: TRenderTitleCell) => {
        const value = (row as TDashboard)?.type as string;
        return <DashboardTypeChip type={value} />;
      },
    },
    {
      field: 'creator',
      headerName: t('blox.dashboards.custom_dashboards.search.columns.created_by'),
      flex: 1,
      maxWidth: 250,
      sortable: true,
      renderCell: ({ row }: TRenderTitleCell) => {
        return <DashboardOwnerField dashboard={row as TDashboard} />;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: () => <SearchRowActionsCell />,
    },
  ];
}
